import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Input, Select, Table, Tag, Button, Drawer, Space, Descriptions, Tooltip } from 'antd';
import { SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, ReloadOutlined, CopyOutlined } from '@ant-design/icons';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { shadowLinkApi } from '../../services/api';

const { Option } = Select;

// 模拟数据
const mockData = [
  {
    id: 'RT-2024-001234',
    businessDomain: '采购',
    businessScenario: '采购RT审核-shadow',
    semanticSimilarity: 0.92,
    assertionResult: 'CONSISTENT',
    reportTime: '2024-01-15 10:30:00',
    agentId: 'procurement_rt_agent',
    aiDecision: '通过',
    aiReasons: [
      { code: 'R001', text: '供应商资质符合要求' },
      { code: 'R002', text: '采购金额在授权范围内' },
    ],
    humanDecision: '通过',
    humanOpinion: '供应商资质齐全，价格合理，同意通过',
  },
  {
    id: 'RT-2024-001235',
    businessDomain: '采购',
    businessScenario: '采购RT审核-shadow',
    semanticSimilarity: 0.45,
    assertionResult: 'INCONSISTENT',
    reportTime: '2024-01-15 11:15:00',
    agentId: 'procurement_rt_agent',
    aiDecision: '拒绝',
    aiReasons: [
      { code: 'R003', text: '供应商资质不符合要求' },
      { code: 'R004', text: '采购金额超出授权范围' },
    ],
    humanDecision: '通过',
    humanOpinion: '供应商资质齐全，价格合理，同意通过',
  },
  {
    id: 'CT-2024-005678',
    businessDomain: '合同',
    businessScenario: '合同机审-shadow',
    semanticSimilarity: 0.88,
    assertionResult: 'CONSISTENT',
    reportTime: '2024-01-15 14:20:00',
    agentId: 'contract_review_agent',
    aiDecision: '拒绝',
    aiReasons: [
      { code: 'R005', text: '合同条款存在风险' },
    ],
    humanDecision: '拒绝',
    humanOpinion: '合同条款确实存在风险，需要修改',
  },
  {
    id: 'RT-2024-001236',
    businessDomain: '采购',
    businessScenario: '采购RT审核-shadow',
    semanticSimilarity: 0,
    assertionResult: 'FAILED',
    reportTime: '2024-01-15 16:45:00',
    agentId: 'procurement_rt_agent',
    aiDecision: '-',
    aiReasons: [],
    humanDecision: '通过',
    humanOpinion: '供应商资质齐全，价格合理，同意通过',
    errorMsg: '服务调用超时',
  },
];

function ShadowReport() {
  const { currentScenario } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(undefined);
  const [selectedScenario, setSelectedScenario] = useState(undefined);
  const [selectedResult, setSelectedResult] = useState(undefined);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  // 筛选选项数据
  const [domainOptions, setDomainOptions] = useState([]);
  const [scenarioOptions, setScenarioOptions] = useState([]);

  // 统计数据
  const stats = {
    total: dataSource.length,
    consistent: dataSource.filter(item => item.assertionResult === 'CONSISTENT').length,
    inconsistent: dataSource.filter(item => item.assertionResult === 'INCONSISTENT').length,
    consistentRate: dataSource.length > 0 
      ? Math.round((dataSource.filter(item => item.assertionResult === 'CONSISTENT').length / dataSource.length) * 100)
      : 0,
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // 构建请求参数，过滤空字符串和 undefined
      const params = {};
      if (searchKeyword) params.keyword = searchKeyword;
      if (selectedDomain) params.businessDomain = selectedDomain;
      if (selectedScenario) params.businessScenario = selectedScenario;
      if (selectedResult) params.assertionResult = selectedResult;

      const response = await shadowLinkApi.getReportList(params);
      setDataSource(response?.list || []);
    } catch (error) {
      console.error('获取影子上报数据失败:', error);
      // 接口异常时使用mock数据（开发调试）
      setDataSource(mockData);
    } finally {
      setLoading(false);
    }
  };

  // 获取业务域选项
  const fetchDomainOptions = async () => {
    try {
      const res = await shadowLinkApi.getBusinessDomains();
      setDomainOptions(res?.list || []);
    } catch (error) {
      console.error('获取业务域列表失败:', error);
      // 接口异常时使用mock数据（开发调试）
      setDomainOptions([
        { value: '采购', label: '采购' },
        { value: '合同', label: '合同' },
        { value: '财务', label: '财务' },
      ]);
    }
  };

  // 获取业务场景选项
  const fetchScenarioOptions = async (domain) => {
    try {
      const res = await shadowLinkApi.getBusinessScenarios(domain);
      setScenarioOptions(res?.list || []);
    } catch (error) {
      console.error('获取业务场景列表失败:', error);
      // 接口异常时使用mock数据（开发调试）
      if (domain === '采购') {
        setScenarioOptions([
          { value: '采购RT审核-shadow', label: '采购RT审核-shadow' },
        ]);
      } else if (domain === '合同') {
        setScenarioOptions([
          { value: '合同机审-shadow', label: '合同机审-shadow' },
        ]);
      } else {
        setScenarioOptions([
          { value: '采购RT审核-shadow', label: '采购RT审核-shadow' },
          { value: '合同机审-shadow', label: '合同机审-shadow' },
        ]);
      }
    }
  };

  // 处理业务域变更
  const handleDomainChange = (value) => {
    setSelectedDomain(value);
    setSelectedScenario(undefined); // 清空已选业务场景
    if (value) {
      fetchScenarioOptions(value);
    } else {
      fetchScenarioOptions(); // 获取全部业务场景
    }
  };

  useEffect(() => {
    fetchData();
    fetchDomainOptions();
    fetchScenarioOptions();
  }, []);

  const handleSearch = () => {
    fetchData();
  };

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailVisible(true);
  };

  const getAssertionResultTag = (result) => {
    switch (result) {
      case 'CONSISTENT':
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            一致
          </Tag>
        );
      case 'INCONSISTENT':
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            不一致
          </Tag>
        );
      case 'FAILED':
        return (
          <Tag icon={<ExclamationCircleOutlined />} color="default">
            断言失败
          </Tag>
        );
      default:
        return <Tag>未知</Tag>;
    }
  };

  const columns = [
    {
      title: '用例ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      ellipsis: true,
      render: (text) => <span className="text-blue-600 font-medium" title={text}>{text}</span>,
    },
    {
      title: '业务域',
      dataIndex: 'businessDomain',
      key: 'businessDomain',
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '业务场景',
      dataIndex: 'businessScenario',
      key: 'businessScenario',
      width: 180,
      ellipsis: true,
      render: (text) => <Tag color="purple">{text?.replace(/-shadow$/i, '')}</Tag>,
    },
    {
      title: 'AI决策',
      dataIndex: 'aiDecision',
      key: 'aiDecision',
      width: 150,
      ellipsis: true,
      render: (value, record) => {
        const hasValue = value && value !== '-' && String(value).trim() !== '';
        const displayText = hasValue ? value : '--';
        if (!hasValue) {
          return <span>--</span>;
        }
        const jsonContent = JSON.stringify(record.aiDecisionJson || value, null, 2);
        // 截断显示文本，最多显示15个字符
        const truncatedText = displayText.length > 15 ? displayText.substring(0, 15) + '...' : displayText;
        return (
          <Tooltip
            title={
              <div className="max-w-md">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">AI决策 (JSON)</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(jsonContent);
                    }}
                    className="text-blue-400"
                  >
                    复制
                  </Button>
                </div>
                <pre className="text-xs text-gray-100 overflow-auto max-h-60">{jsonContent}</pre>
              </div>
            }
            color="#1f2937"
            overlayStyle={{ maxWidth: 500 }}
          >
            <span className="cursor-pointer text-gray-700 hover:text-gray-900 inline-block" style={{ maxWidth: '130px' }} title={displayText}>
              {truncatedText}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '人工决策',
      dataIndex: 'humanDecision',
      key: 'humanDecision',
      width: 150,
      ellipsis: true,
      render: (value, record) => {
        const hasValue = value && value !== '-' && String(value).trim() !== '';
        const displayText = hasValue ? value : '--';
        if (!hasValue) {
          return <span>--</span>;
        }
        const jsonContent = JSON.stringify(record.humanDecisionJson || value, null, 2);
        // 截断显示文本，最多显示15个字符
        const truncatedText = displayText.length > 15 ? displayText.substring(0, 15) + '...' : displayText;
        return (
          <Tooltip
            title={
              <div className="max-w-md">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">人工决策 (JSON)</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(jsonContent);
                    }}
                    className="text-blue-400"
                  >
                    复制
                  </Button>
                </div>
                <pre className="text-xs text-gray-100 overflow-auto max-h-60">{jsonContent}</pre>
              </div>
            }
            color="#1f2937"
            overlayStyle={{ maxWidth: 500 }}
          >
            <span className="cursor-pointer text-gray-700 hover:text-gray-900 inline-block" style={{ maxWidth: '130px' }} title={displayText}>
              {truncatedText}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '决策一致性',
      dataIndex: 'assertionResult',
      key: 'assertionResult',
      width: 120,
      render: (result) => getAssertionResultTag(result),
    },
    {
      title: '上报时间',
      dataIndex: 'reportTime',
      key: 'reportTime',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  const getRowClassName = (record) => {
    if (record.assertionResult === 'INCONSISTENT') {
      return 'bg-red-50';
    }
    return '';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="page-title">影子数据上报</h1>
            <p className="page-description">展示影子链路上报数据的断言结果，支持查询和详情查看</p>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            刷新
          </Button>
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="总断言数"
              value={stats.total}
              suffix="条"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="断言一致数"
              value={stats.consistent}
              suffix="条"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="断言不一致数"
              value={stats.inconsistent}
              suffix="条"
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="整体一致率"
              value={stats.consistentRate}
              suffix="%"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选栏 */}
      <Card className="mb-4">
        <Space size="middle">
          <Input
            placeholder="搜索单据ID / 业务场景"
            prefix={<SearchOutlined />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 250 }}
          />
          <Select
            placeholder="筛选业务域"
            value={selectedDomain}
            onChange={handleDomainChange}
            style={{ width: 150 }}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={domainOptions}
          />
          <Select
            placeholder={selectedDomain ? '筛选业务场景' : '请先选择业务域'}
            value={selectedScenario}
            onChange={setSelectedScenario}
            style={{ width: 200 }}
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            disabled={!selectedDomain}
            options={scenarioOptions}
          />
          <Select
            placeholder="筛选断言结果"
            value={selectedResult}
            onChange={setSelectedResult}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="CONSISTENT">一致</Option>
            <Option value="INCONSISTENT">不一致</Option>
            <Option value="FAILED">断言失败</Option>
          </Select>
          <Button type="primary" onClick={handleSearch}>
            查询
          </Button>
        </Space>
      </Card>

      {/* 数据列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          rowClassName={getRowClassName}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 详情抽屉 */}
      <Drawer
        title={
          <Space>
            <span>上报详情 - {selectedRecord?.id}</span>
          </Space>
        }
        width={720}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedRecord && (
          <>
            <Descriptions
              title="基本信息"
              bordered
              column={2}
              className="mb-6"
            >
              <Descriptions.Item label="单据ID" span={2}>{selectedRecord.id}</Descriptions.Item>
              <Descriptions.Item label="业务域">
                <Tag color="blue">{selectedRecord.businessDomain}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="业务场景">
                <Tag color="purple">{selectedRecord.businessScenario}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Agent ID" span={2}>{selectedRecord.agentId}</Descriptions.Item>
              <Descriptions.Item label="AI决策" span={2}>
                {(() => {
                  const value = selectedRecord.aiDecision;
                  const hasValue = value && value !== '-' && String(value).trim() !== '';
                  if (!hasValue) return '--';
                  const jsonContent = JSON.stringify(selectedRecord.aiDecisionJson || value, null, 2);
                  // 截断显示文本，最多显示30个字符
                  const truncatedText = value.length > 30 ? value.substring(0, 30) + '...' : value;
                  return (
                    <Tooltip
                      title={
                        <div className="max-w-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-300">AI决策 (JSON)</span>
                            <Button
                              type="link"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                navigator.clipboard.writeText(jsonContent);
                              }}
                              className="text-blue-400"
                            >
                              复制
                            </Button>
                          </div>
                          <pre className="text-xs text-gray-100 overflow-auto max-h-60">{jsonContent}</pre>
                        </div>
                      }
                      color="#1f2937"
                      overlayStyle={{ maxWidth: 500 }}
                    >
                      <span className="cursor-pointer text-gray-700 hover:text-gray-900 truncate inline-block" style={{ maxWidth: '300px' }} title={value}>
                        {truncatedText}
                      </span>
                    </Tooltip>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="人工决策" span={2}>
                {(() => {
                  const value = selectedRecord.humanDecision;
                  const hasValue = value && value !== '-' && String(value).trim() !== '';
                  if (!hasValue) return '--';
                  const jsonContent = JSON.stringify(selectedRecord.humanDecisionJson || value, null, 2);
                  // 截断显示文本，最多显示30个字符
                  const truncatedText = value.length > 30 ? value.substring(0, 30) + '...' : value;
                  return (
                    <Tooltip
                      title={
                        <div className="max-w-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-300">人工决策 (JSON)</span>
                            <Button
                              type="link"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                navigator.clipboard.writeText(jsonContent);
                              }}
                              className="text-blue-400"
                            >
                              复制
                            </Button>
                          </div>
                          <pre className="text-xs text-gray-100 overflow-auto max-h-60">{jsonContent}</pre>
                        </div>
                      }
                      color="#1f2937"
                      overlayStyle={{ maxWidth: 500 }}
                    >
                      <span className="cursor-pointer text-gray-700 hover:text-gray-900 truncate inline-block" style={{ maxWidth: '300px' }} title={value}>
                        {truncatedText}
                      </span>
                    </Tooltip>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="决策一致性" span={2}>
                {getAssertionResultTag(selectedRecord.assertionResult)}
              </Descriptions.Item>
              <Descriptions.Item label="上报时间" span={2}>{selectedRecord.reportTime}</Descriptions.Item>
            </Descriptions>

            {/* AI审核理由 */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <span className="w-1 h-5 bg-blue-500 rounded mr-2"></span>
                AI 审核理由
              </h3>
              {selectedRecord.assertionResult === 'FAILED' ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500">
                  断言执行失败：{selectedRecord.errorMsg || '未知错误'}
                </div>
              ) : selectedRecord.aiReasons === undefined || selectedRecord.aiReasons === null ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500">
                  无AI审核理由
                </div>
              ) : Array.isArray(selectedRecord.aiReasons) ? (
                <div className="space-y-3">
                  {selectedRecord.aiReasons.map((reason, index) => (
                    <div
                      key={index}
                      className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start"
                    >
                      <CheckCircleOutlined className="text-green-500 mt-1 mr-3" />
                      <div>
                        <span className="text-green-700 font-medium">[{reason.code}]</span>
                        <span className="text-gray-700 ml-2">{reason.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : typeof selectedRecord.aiReasons === 'object' ? (
                <div className="space-y-3">
                  {Object.entries(selectedRecord.aiReasons).map(([code, text], index) => (
                    <div
                      key={index}
                      className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start"
                    >
                      <CheckCircleOutlined className="text-green-500 mt-1 mr-3" />
                      <div>
                        <span className="text-green-700 font-medium">[{code}]</span>
                        <span className="text-gray-700 ml-2">{text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                  <CheckCircleOutlined className="text-green-500 mt-1 mr-3" />
                  <p className="text-gray-700">{String(selectedRecord.aiReasons)}</p>
                </div>
              )}
            </div>

            {/* 人工审核意见 */}
            <div>
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <span className="w-1 h-5 bg-orange-500 rounded mr-2"></span>
                人工审核意见
              </h3>
              {selectedRecord.humanOpinion === undefined || selectedRecord.humanOpinion === null ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500">
                  无人工审核意见
                </div>
              ) : Array.isArray(selectedRecord.humanOpinion) ? (
                <div className="space-y-3">
                  {selectedRecord.humanOpinion.map((opinion, index) => (
                    <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-gray-700">{opinion}</p>
                    </div>
                  ))}
                </div>
              ) : typeof selectedRecord.humanOpinion === 'object' ? (
                <div className="space-y-3">
                  {Object.entries(selectedRecord.humanOpinion).map(([key, value], index) => (
                    <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-gray-700"><span className="font-medium">{key}:</span> {value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-gray-700">{String(selectedRecord.humanOpinion)}</p>
                </div>
              )}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}

export default ShadowReport;
